import React, { Fragment } from 'react'
import {
  Typography, SpacerVertical,
  View
} from '@klarna/bubble-ui'
import { Node, FiscProps, FiscPaymentDetailsProps } from '../types'
import getValueFromNodes from '../utilities/getValueFromNode'
import PaymentStepsTimeline from '../components/PaymentStepsTimeline/PaymentStepsTimeline'
import config from '../config'
import LegalText from '../components/LegalText/LegalText'
import PaymentMethodContainer, { PaymentMethodProps } from './PaymentMetodContainer/PaymentMethodContainer'
import { isAmountGreaterThanZero, isValidAmount } from '../utilities/amountUtils'
import { parseLocaleNumber } from '../utilities/numberUtils'
import RiskDisclaimer from '../components/RiskDisclaimer/RiskDeclaimer'
import { KlarnaBadge } from '../components/KlarnaBadge/KlarnaBadge'
import data from './translations/temp.json'
import { CountryCode } from '../../types/Localization'

type Translations = {
  [key: string]: {
    [key: string]: string
  }
}

type PaymentCalculatorProps = {
  nodes: Node[]
  theme: string
  locale: string
  intents?: string[] | string
}

const paymentOptionNormalizer = (node: PaymentMethodProps, name: string, noOfInstallement?: string | null) => ({
  name,
  installmentAmount: node.installmentAmount || node.purchaseAmount,
  noOfInstallement: noOfInstallement || '',
})

const getNormalizedLocale = (locale: string) => locale.replace('payment-calculator-interstitial-revamped/', '').replace('_', '-')

type FiscBadgeNode = Node & { no_of_installments?: string }

// eslint-disable-next-line @typescript-eslint/naming-convention
const PaymentCalculatorRevamped: React.FC<PaymentCalculatorProps> = ({
  nodes, theme, locale, intents
}) => {
  const translations = data as Translations
  const backgroundColorKey = theme === 'dark' ? 'darkBackgroundTheme' : 'backgroundTheme'
  const { frame_component_revamped: frameBackground, default: riskDisclaimerBackground } = config[backgroundColorKey]
  const fiscInstallments = nodes
    .filter((node): node is FiscBadgeNode => node.name.includes('FIXED_SUM_CREDIT_BADGE'))
    .map(node => node.no_of_installments)
    .filter((installment): installment is string => Boolean(installment))
  const country = getNormalizedLocale(locale).split('-')[1].toUpperCase() as CountryCode

  let isSubscriptionOrAddToWallet = false
  if (intents) {
    if (Array.isArray(intents)) {
      isSubscriptionOrAddToWallet = intents?.some(intent => ['SUBSCRIBE', 'ADD_TO_WALLET'].includes(intent))
    } else {
      isSubscriptionOrAddToWallet = ['SUBSCRIBE', 'ADD_TO_WALLET'].includes(intents)
    }
  }

  let fiscLegalText = translations[locale]?.FIXED_SUM_CREDIT_LEGAL_TEXT as string
  const genericLegalText = translations[locale]?.GENERIC_LEGAL_TEXT as string

  let displayAPRText = false
  const getAprTextForFISCTier = (riskBasedPricing: string, apr: string, overallMinApr: string, overallMaxApr: string) => {
    [apr, overallMinApr, overallMaxApr].forEach((aprValue, index) => {
      fiscLegalText = fiscLegalText.replace(`{{apr${index}}}`, aprValue)
    })

    if (riskBasedPricing === 'true') {
      return (overallMinApr === overallMaxApr) ? `${overallMinApr}%` : `${overallMinApr}% - ${overallMaxApr}%`
    }
    return `${apr}%`
  }

  // Select the highest representative APR value among FISC installments
  const getHighestAprValueFromFiscInstallments = (installments: string[]): string => {
    const aprValues = installments
      .map(installment => getValueFromNodes(nodes, `FIXED_SUM_CREDIT_APR_${installment}`))
      .filter((value): value is string => typeof value === 'string')

    if (aprValues.length === 0) {
      return '0'
    }

    const validAprs = aprValues
      .map(value => ({
        original: value,
        parsed: parseLocaleNumber(value, getNormalizedLocale(locale))
      }))
      .filter(item => !Number.isNaN(item.parsed))

    if (validAprs.length === 0) {
      return '0'
    }

    const highestApr = validAprs.reduce((max, current) => (current.parsed > max.parsed ? current : max))

    return highestApr.original
  }

  const normalizedPaymentOptions = []

  const payByCardProps = {
    badge:
      getValueFromNodes(nodes, 'PAY_BY_CARD_BADGE')
      || getValueFromNodes(nodes, 'CARD_BADGE')
      || getValueFromNodes(nodes, 'DIRECT_DEBIT_BADGE')
      || getValueFromNodes(nodes, 'BANK_TRANSFER_BADGE'),
    purchaseAmount: getValueFromNodes(nodes, 'PURCHASE_AMOUNT'),
    frequencyTitle: isSubscriptionOrAddToWallet
      ? translations[locale]?.FREQUENCY_PAY_BY_CARD_TOKENIZATION
      : translations[locale]?.FREQUENCY_PAY_BY_CARD,
    backgroundColorKey,
  }
  const payByCardDetailsProps = {
    type: 'payByCard' as const,
    totalTranslation: getValueFromNodes(nodes, 'TOTAL_TRANSLATION'),
    purchaseAmount: getValueFromNodes(nodes, 'PURCHASE_AMOUNT'),
    totalAmount: getValueFromNodes(nodes, 'PURCHASE_AMOUNT')
  }
  normalizedPaymentOptions.push(paymentOptionNormalizer(payByCardProps, 'PAY_BY_CARD', null))

  const invoiceProps = {
    badge: isSubscriptionOrAddToWallet
      ? translations[locale]?.INVOICE_BADGE_TOKENIZATION
      : getValueFromNodes(nodes, 'INVOICE_BADGE'),
    purchaseAmount: getValueFromNodes(nodes, 'PURCHASE_AMOUNT'),
    frequencyTitle: isSubscriptionOrAddToWallet
      ? translations[locale]?.FREQUENCY_INVOICE_TOKENIZATION
      : getValueFromNodes(nodes, 'INVOICE_FREQUENCY_TITLE') ?? translations[locale]?.FREQUENCY_INVOICE,
    backgroundColorKey,
  }
  normalizedPaymentOptions.push(paymentOptionNormalizer(invoiceProps, 'INVOICE', null))
  const invoiceFeeAmount = getValueFromNodes(nodes, 'INVOICE_FEE_AMOUNT')
  const invoiceDetailsProps = {
    type: 'invoice' as const,
    totalTranslation: getValueFromNodes(nodes, 'TOTAL_TRANSLATION'),
    purchaseAmount: getValueFromNodes(nodes, 'PURCHASE_AMOUNT'),
    totalAmount: getValueFromNodes(nodes, 'INVOICE_TOTAL_AMOUNT'),
    feeAmount: invoiceFeeAmount,
    feeTranslation: getValueFromNodes(nodes, 'INVOICE_FEE_TRANSLATION') || '',
    isFeeGreaterThanZero: isAmountGreaterThanZero(invoiceFeeAmount)
  }

  const payLaterByCardProps = {
    badge: getValueFromNodes(nodes, 'PAY_LATER_BY_CARD_BADGE'),
    purchaseAmount: getValueFromNodes(nodes, 'PURCHASE_AMOUNT'),
    frequencyTitle: isSubscriptionOrAddToWallet
      ? translations[locale]?.FREQUENCY_INVOICE_TOKENIZATION
      : translations[locale]?.FREQUENCY_INVOICE,
    backgroundColorKey,
  }
  normalizedPaymentOptions.push(paymentOptionNormalizer(payLaterByCardProps, 'PAY_LATER_BY_CARD', null))

  const calculatedNumberOfInstallments = getValueFromNodes(nodes, 'SLICE_IT_BY_CARD_BREAKDOWN')?.split('-')?.length

  const sibcProps = {
    badge: getValueFromNodes(nodes, 'SLICE_IT_BY_CARD_BADGE'),
    frequencySteps: getValueFromNodes(nodes, 'SLICE_IT_BY_CARD_FREQUENCY_STEPS'),
    frequencyTitle: isSubscriptionOrAddToWallet
      ? translations[locale]?.[`FREQUENCY_SLICE_IT_BY_CARD_${calculatedNumberOfInstallments}_TOKENIZATION`]
      : translations[locale]?.[`FREQUENCY_SLICE_IT_BY_CARD_${calculatedNumberOfInstallments}`],
    breakdown: getValueFromNodes(nodes, 'SLICE_IT_BY_CARD_BREAKDOWN'),
    installmentAmount: getValueFromNodes(nodes, 'SLICE_IT_BY_CARD_INSTALLMENT_AMOUNT'),
    purchaseAmount: getValueFromNodes(nodes, 'PURCHASE_AMOUNT'),
    backgroundColorKey,
    snippet: getValueFromNodes(nodes, 'SUBSCRIBE_SLICE_IT_BY_CARD_SNIPPET')
  }
  normalizedPaymentOptions.push(paymentOptionNormalizer(sibcProps, 'SLICE_IT_BY_CARD', `${sibcProps.frequencySteps?.split(',')?.length}`))

  const interestAmount = getValueFromNodes(nodes, 'SLICE_IT_BY_CARD_INTEREST_AMOUNT')
  const sibcDetailsProps = {
    type: 'installment' as const,
    installmentsTotal: getValueFromNodes(nodes, 'PURCHASE_AMOUNT'),
    totalTranslation: getValueFromNodes(nodes, 'TOTAL_TRANSLATION'),
    interestTranslation: getValueFromNodes(nodes, 'INTEREST_TRANSLATION'),
    interestFreeTranslation: translations[locale]?.INTEREST_FREE_TRANSLATION,
    interestAmount: isValidAmount(interestAmount) ? interestAmount : null,
    isInterestGreaterThanZero: isAmountGreaterThanZero(interestAmount),
    apr: getValueFromNodes(nodes, 'SLICE_IT_BY_CARD_APR'),
    aprParagraph: getValueFromNodes(nodes, 'SLICE_IT_BY_CARD_APR_PARAGRAPH'),
    aprTranslation: getValueFromNodes(nodes, 'APR_TRANSLATION')
  }

  const fiscProps: Record<string, FiscProps> = {}
  const fiscDetailsProps: Record<string, FiscPaymentDetailsProps> = {}

  const isRiskBasedPricing = getValueFromNodes(nodes, 'FIXED_SUM_CREDIT_WITH_RISKBASED_PRICING')
  const maxApr = getValueFromNodes(nodes, 'FIXED_SUM_CREDIT_OVERALL_MAX_APR')
  const minApr = getValueFromNodes(nodes, 'FIXED_SUM_CREDIT_OVERALL_MIN_APR')

  const aprText = getAprTextForFISCTier(
    isRiskBasedPricing,
    getHighestAprValueFromFiscInstallments(fiscInstallments),
    minApr,
    maxApr
  )

  fiscInstallments.forEach(installment => {
    const downPayment = getValueFromNodes(nodes, `FIXED_SUM_CREDIT_AMOUNT_DOWNPAYMENT_${installment}`)
    const finalPayment = getValueFromNodes(nodes, `FIXED_SUM_CREDIT_AMOUNT_FINALPAYMENT_${installment}`)
    const ballonPercentage = getValueFromNodes(nodes, `FIXED_SUM_CREDIT_BALOON_PERCENTAGE_${installment}`)

    fiscProps[installment] = {
      badge: getValueFromNodes(nodes, `FIXED_SUM_CREDIT_BADGE_${installment}`),
      purchaseAmount: getValueFromNodes(nodes, 'PURCHASE_AMOUNT'),
      frequencyTitle: translations[locale]?.FREQUENCY_FIXED_SUM_CREDIT,
      installmentAmount: getValueFromNodes(nodes, `FIXED_SUM_CREDIT_INSTALLMENT_MONTHLY_${installment}`),
      backgroundColorKey,
    }

    normalizedPaymentOptions.push(paymentOptionNormalizer(fiscProps[installment], 'FIXED_SUM_CREDIT', installment))

    fiscDetailsProps[installment] = {
      type: 'fisc' as const,
      totalTranslation: getValueFromNodes(nodes, 'TOTAL_TRANSLATION'),
      apr: getValueFromNodes(nodes, `FIXED_SUM_CREDIT_APR_${installment}`),
      aprParagraph: aprText,
      aprTranslation: getValueFromNodes(nodes, 'APR_TRANSLATION'),
      interestAmount: getValueFromNodes(nodes, `FIXED_SUM_CREDIT_INTEREST_AMOUNT_${installment}`),
      interestTranslation: getValueFromNodes(nodes, 'INTEREST_TRANSLATION'),
      downPayment: isAmountGreaterThanZero(downPayment) ? downPayment : null,
      downPaymentTranslation: getValueFromNodes(nodes, 'DOWN_PAYMENT_TRANSLATION'),
      dueTodayTranslation: translations[locale]?.DOWN_PAYMENT_DUE_TODAY || getValueFromNodes(nodes, 'DOWN_PAYMENT_TRANSLATION'),
      finalPayment: isAmountGreaterThanZero(ballonPercentage) ? finalPayment : null,
      finalPaymentTranslation: getValueFromNodes(nodes, 'FINAL_PAYMENT_TRANSLATION'),
      installmentsTotal: getValueFromNodes(nodes, `FIXED_SUM_CREDIT_TOTAL_AMOUNT_${installment}`),
      interestFreeTranslation: `${translations[locale]?.INTEREST_FREE_TRANSLATION}${country === 'US' ? '*' : ''}`,
      isConditionalApr: ['US', 'ES'].includes(country),
      showApr: country === 'ES',
    }

    fiscDetailsProps[installment].isInterestGreaterThanZero = isAmountGreaterThanZero(fiscDetailsProps[installment].interestAmount)

    displayAPRText = true
  })

  const legalText = `${(displayAPRText && fiscLegalText) ? fiscLegalText : genericLegalText}`

  const sortedPaymentOptions = normalizedPaymentOptions.sort((a, b) => {
    if (a.installmentAmount !== b.installmentAmount) {
      const amountA = parseLocaleNumber(a.installmentAmount, getNormalizedLocale(locale))
      const amountB = parseLocaleNumber(b.installmentAmount, getNormalizedLocale(locale))
      return amountA - amountB
    }
    const aInstallment = parseFloat(a.noOfInstallement) || 0
    const bInstallment = parseFloat(b.noOfInstallement) || 0
    return bInstallment - aInstallment
  })

  const hasFiscWithBadge = Object.values(fiscProps).some(prop => !!prop.badge)
  const hasSibcWithBadge = !!sibcProps.badge
  const shouldShowTimeline = hasFiscWithBadge || hasSibcWithBadge

  return (
    <Fragment>
      <View style={{ display: 'flex', alignItems: 'center' }}>
        <KlarnaBadge width="100" height="45" className="badge-revamped" />
      </View>
      <SpacerVertical spaceToken="space.200" />
      <Typography
        id="payment-calculator-title"
        accessibilityPreset="heading 1"
        textToken="text-style/headings/titles/bold/tertiary"
        textAlignToken="text-alignment/center"
      >
        {translations[locale]?.TITLE}
      </Typography>
      <SpacerVertical spaceToken="space.100" />

      <Typography
        accessibilityPreset="paragraph"
        textToken="text-style/text/super-paragraphs/body/regular"
        colorToken="colors/text/subtle"
        textAlignToken="text-alignment/center"
      >
        {translations[locale]?.SUB_TITLE}
      </Typography>

      <SpacerVertical spaceToken="space.500" />

      <Typography
        accessibilityPreset="heading 2"
        textToken="text-style/text/labels/body/medium"
        colorToken="colors/text/subtle"
        textAlignToken="text-alignment/center"
        id="payment-calculator-offering-header"
      >
        {translations[locale]?.OFFERING_HEADER}
      </Typography>
      <SpacerVertical spaceToken="space.200" />

      <View role="list" aria-labelledby="payment-calculator-offering-header">
        {
          sortedPaymentOptions.map((pm, index) => {
            if (pm.name === 'PAY_BY_CARD') {
              return <PaymentMethodContainer
                key={`${pm.name}-${index}`}
                frameBackground={frameBackground}
                paymentMethodProps={payByCardProps}
                paymentDetailsProps={payByCardDetailsProps}
                isSubscriptionOrAddToWallet={isSubscriptionOrAddToWallet}
              />
            }

            if (pm.name === 'INVOICE') {
              return <PaymentMethodContainer
                key={`${pm.name}-${index}`}
                frameBackground={frameBackground}
                paymentMethodProps={invoiceProps}
                paymentDetailsProps={invoiceDetailsProps}
                isSubscriptionOrAddToWallet={isSubscriptionOrAddToWallet}
              />
            }

            if (pm.name === 'PAY_LATER_BY_CARD') {
              return <PaymentMethodContainer
                key={`${pm.name}-${index}`}
                frameBackground={frameBackground}
                paymentMethodProps={payLaterByCardProps}
                paymentDetailsProps={sibcDetailsProps}
                isSubscriptionOrAddToWallet={isSubscriptionOrAddToWallet}
              />
            }

            if (pm.name === 'SLICE_IT_BY_CARD') {
              return <PaymentMethodContainer
                key={`${pm.name}-${index}`}
                frameBackground={frameBackground}
                paymentMethodProps={sibcProps}
                paymentDetailsProps={sibcDetailsProps}
                isSubscriptionOrAddToWallet={isSubscriptionOrAddToWallet}
              />
            }

            if (pm.name === 'FIXED_SUM_CREDIT' && !isSubscriptionOrAddToWallet) {
              return <PaymentMethodContainer
                key={`${pm.name}-${index}`}
                frameBackground={frameBackground}
                paymentMethodProps={fiscProps[pm.noOfInstallement]}
                paymentDetailsProps={fiscDetailsProps[pm.noOfInstallement]}
              />
            }

            return null
          })
        }
      </View>

      <SpacerVertical spaceToken="space.400" />
      {shouldShowTimeline ? (
        <PaymentStepsTimeline
          nodes={nodes}
          titleToken="text-style/headings/titles/bold/grande"
          headerToken="text-style/text/super-paragraphs/body/medium"
          paragraphColorToken="colors/text/subtle"
        />
      ) : (
        <></>
      )}
      <SpacerVertical spaceToken="space.400" />

      <RiskDisclaimer
        frameBackground={riskDisclaimerBackground}
        riskDesclaimerHeading={
          getValueFromNodes(
            nodes,
            'FIXED_SUM_CREDIT_RISK_DISCLAIMER_HEADING'
          ) || ''
        }
        riskDisclaimerSubHeading={
          getValueFromNodes(
            nodes,
            'FIXED_SUM_CREDIT_RISK_DISCLAIMER_SUBHEADING'
          ) || ''
        }
        riskDesclaimerSource={
          getValueFromNodes(nodes, 'FIXED_SUM_CREDIT_RISK_DISCLAIMER_SOURCE')
          || ''
        }
      />

      <View alignText="text-alignment/center">
        <LegalText alignText="text-alignment/center" legalText={legalText} />
      </View>
    </Fragment>
  )
}

export default PaymentCalculatorRevamped
